import { BlockedActionTooltip, Button, cn, DropdownMenuItem, Icon, MoreMenu, Spinner } from "@qw/ui";
import type { FC } from "react";
import { OVER_ATTACHMENT_LIMIT_HINT, useDraft } from "@/providers/draft-provider";

/**
 * Mirrors the server's `formatAttachmentBytes` (api `email-drafting.utils.ts`): round *up* to
 * 0.1 MB so a total a few KB over the cap reads above it. `formatFileSize` rounds to the nearest
 * hundredth, which renders a small overage as "25 MB of 25 MB — too large to send".
 */
const formatAttachmentSize = (bytes: number) => `${Math.ceil((bytes / (1024 * 1024)) * 10) / 10} MB`;

export const DraftCardFooterExtras: FC = () => (
  <>
    <DraftMoreMenu />
    <AttachmentSizePill />
    <DeleteDraftButton />
  </>
);

const DraftMoreMenu: FC = () => {
  const { isScheduling, isSending, isSyncing, isDeleting } = useDraft();

  return (
    <MoreMenu
      align="start"
      buttonProps={{ variant: "outline", size: "sm", disabled: isScheduling || isSending || isSyncing || isDeleting }}
      isLoading={isScheduling || isSyncing}
    >
      <ScheduleSendDraftMenuItem />
      <EditDraftInGmailMenuItem />
    </MoreMenu>
  );
};

const ScheduleSendDraftMenuItem: FC = () => {
  const { handleScheduleSendDraft, isScheduling, attachmentSize } = useDraft();
  const exceedsSizeLimit = attachmentSize?.exceedsSizeLimit ?? false;

  // `block` keeps the menu row full-width; the default inline wrapper would shrink-wrap it.
  return (
    <BlockedActionTooltip reason={exceedsSizeLimit ? OVER_ATTACHMENT_LIMIT_HINT : undefined} className="block">
      <DropdownMenuItem onClick={handleScheduleSendDraft} disabled={isScheduling || exceedsSizeLimit}>
        {isScheduling ? (
          <>
            <Spinner className="size-3" />
            Scheduling&hellip;
          </>
        ) : (
          <>
            <Icon icon="schedule_send" />
            Schedule send
          </>
        )}
      </DropdownMenuItem>
    </BlockedActionTooltip>
  );
};

const EditDraftInGmailMenuItem: FC = () => {
  const { handleEditInGmail, isSyncing } = useDraft();

  return (
    <DropdownMenuItem onClick={handleEditInGmail} disabled={isSyncing}>
      {isSyncing ? (
        <>
          <Spinner className="size-3" />
          Opening in Gmail&hellip;
        </>
      ) : (
        <>
          <Icon icon="edit" />
          Edit in Gmail
        </>
      )}
    </DropdownMenuItem>
  );
};

/**
 * Sits in the gap between the send controls and Delete. Always renders something so Delete stays
 * right-aligned whether or not there is a size to report. The copy is deliberately short to hold
 * one line at narrow card widths — the Send tooltip carries the full explanation.
 */
const AttachmentSizePill: FC = () => {
  const { attachmentSize } = useDraft();

  if (!attachmentSize || (!attachmentSize.isNearSizeLimit && !attachmentSize.exceedsSizeLimit)) {
    return <span className="flex-1 -mx-2" />;
  }

  const { totalSize, sizeLimit, exceedsSizeLimit } = attachmentSize;
  const sizeLabel = `${formatAttachmentSize(totalSize)} of ${formatAttachmentSize(sizeLimit)}`;

  return (
    <div className="flex min-w-0 flex-1 justify-start">
      <div
        role="status"
        className={cn(
          "flex min-w-0 items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-sm",
          exceedsSizeLimit ? "bg-destructive/10 text-destructive" : "bg-warning/10 text-warning"
        )}
      >
        <Icon icon={exceedsSizeLimit ? "error" : "warning"} className="flex-none text-base" />
        <span className="min-w-0 truncate">{exceedsSizeLimit ? `${sizeLabel} — too large to send` : sizeLabel}</span>
      </div>
    </div>
  );
};

const DeleteDraftButton: FC = () => {
  const { handleDeleteDraft, isSending, isDeleting } = useDraft();

  return (
    <Button
      variant="ghost"
      size="sm"
      display="icon"
      onClick={handleDeleteDraft}
      disabled={isSending || isDeleting}
      className="-mr-2"
    >
      {isDeleting ? <Spinner className="size-3" /> : <Icon icon="delete" />}
    </Button>
  );
};
